import { Box, Typography, type BoxProps } from "@mui/material"
import { Container, Link, ResponsiveImage, type FC, type ResponsiveImageType } from ".."

type Destination = {
  title: string
  link: string
  image: ResponsiveImageType
}

interface DestinationsCollectionProps extends BoxProps {
  items: Destination[]
}

const DestinationsCollection: FC<DestinationsCollectionProps> = ({ items, sx }) => {
  const itemsLength = items?.length

  if (!itemsLength) {
    return null
  }
  return (
    <Container
      mt={{ xs: 10, md: 15 }}
      mb={{ xs: 20, md: 30 }}
      sx={[
        (theme) => ({
          mt: { xs: 10, md: 15 },
          mb: { xs: 20, md: 30 },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box mb={10}>
        <Typography variant="body.md">
          {`Explore ${itemsLength} ${itemsLength > 1 ? "Destinations" : "Destination"}`}
        </Typography>
      </Box>
      <Box
        width="100%"
        display="grid"
        rowGap={{ xs: 2, md: 5 }}
        columnGap={{ xs: 3.5, md: 2.5 }}
        gridTemplateColumns={{ xs: "repeat(3, 1fr)", lg: "repeat(4, 1fr)", xl: "repeat(5, 1fr)" }}
        justifyContent="space-between"
      >
        {items?.map((destination, i) => (
          <DestinationItem key={i} {...destination} />
        ))}
      </Box>
    </Container>
  )
}

export default DestinationsCollection

/** A sub-component for the content section of the content highlight component */
const DestinationItem: FC<Destination> = ({ title, link, image }) => {
  return (
    <Box
      position="relative"
      display="flex"
      gap={{ xs: 4, md: 5 }}
      flexDirection={{ xs: "column", md: "row" }}
      alignItems="center"
      borderRadius={1.5}
      padding={(theme) => ({ xs: theme.spacing(1, 1, 4, 1), md: theme.spacing(2.5, 2.5) })}
      sx={(theme) => ({
        "transition": "0.5s",
        "&:hover": {
          cursor: "pointer",
          boxShadow: "1px 1px 10px 0px rgba(0, 0, 0, 0.15)",
          img: {
            transform: "scale(1.05)",
          },
        },
      })}
    >
      <Link type="overlay" href={link} aria-label={title} />
      <ResponsiveImage
        image={image}
        sx={(theme) => ({
          width: { xs: theme.spacing(25), md: theme.spacing(20) },
          height: { xs: theme.spacing(25), md: theme.spacing(20) },
          borderRadius: theme.spacing(1),
          transition: theme.transitions.create("transform", {
            duration: theme.transitions.duration.standard * 1.5,
          }),
        })}
      />
      <Typography variant="body.lg" component="h2">
        {title}
      </Typography>
    </Box>
  )
}
